import React from 'react';
import { iterateFocusableElements } from '@primer/behaviors/utils';
import { useProvidedRefOrCreate } from './useProvidedRefOrCreate.js';

/*
 * A mnemonic indicates to the user which key to press (single)
 * to activate a command or navigate to a component
 * typically appearing in a menu title, menu item, or the text of a button.
 */

const useMnemonics = (open, providedRef) => {
  const containerRef = useProvidedRefOrCreate(providedRef);
  React.useEffect(function addAriaKeyshortcuts() {
    if (!open || !containerRef.current) return;
    const container = containerRef.current;
    const focusableItems = [...iterateFocusableElements(container)];
    focusableItems.map(item => {
      var _item$textContent;
      // if item already has aria-keyshortcuts defined by user, skip
      if (item.getAttribute('aria-keyshortcuts')) return;
      const firstLetter = (_item$textContent = item.textContent) === null || _item$textContent === undefined ? undefined : _item$textContent.toLowerCase()[0];
      if (firstLetter) item.setAttribute('aria-keyshortcuts', firstLetter);
    });
  }, [open, containerRef]);
  React.useEffect(function handleKeyDown() {
    if (!open || !containerRef.current) return;
    const container = containerRef.current;
    const handler = event => {
      var _elementToFocus;
      // skip if a TextInput has focus
      const activeElement = document.activeElement;
      if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') return;

      // skip if used with modifier to preserve shortcuts like ⌘ + F
      const hasModifier = event.ctrlKey || event.altKey || event.metaKey;
      if (hasModifier) return;

      // skip if it's not a alphabet key
      if (!isAlphabetKey(event)) return;

      // if this is a typeahead event, don't propagate outside of menu
      event.stopPropagation();
      const query = event.key.toLowerCase();
      let elementToFocus;
      const focusableItems = [...iterateFocusableElements(container)];
      const itemsMatchingKey = focusableItems.filter(item => {
        var _item$getAttribute;
        const keyshortcuts = (_item$getAttribute = item.getAttribute('aria-keyshortcuts')) === null || _item$getAttribute === undefined ? undefined : _item$getAttribute.split(' ').map(shortcut => shortcut.toLowerCase());
        return keyshortcuts && keyshortcuts.includes(query);
      });
      const currentActiveIndex = itemsMatchingKey.indexOf(activeElement);

      // If the last element is already selected, cycle through the list
      if (currentActiveIndex === itemsMatchingKey.length - 1) {
        elementToFocus = itemsMatchingKey[0];
      } else {
        elementToFocus = itemsMatchingKey.find((item, index) => {
          return index > currentActiveIndex;
        });
      }
      (_elementToFocus = elementToFocus) === null || _elementToFocus === undefined ? undefined : _elementToFocus.focus();
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }, [open, containerRef]);
  const isAlphabetKey = event => {
    return event.key.length === 1 && /[a-z\d]/i.test(event.key);
  };
  return {
    containerRef
  };
};

export { useMnemonics };
